'use client';

import { type ReactElement, useCallback, useState } from 'react';

import { heicToJpegBlob } from '../../heic-to-blob';
import { ImageCropperDialog } from '../../molecules/ImageCropperDialog';
import type { CropConfig } from '../types/CropConfig';
import type { FileInfo } from '../types/FileInfo';
import type { SupportedFileFormats } from '../types/SupportedFileFormats';
import { FileUploader } from './FileUploader';

// ─── Constants ──────────────────────────────────────────────────────────────

const kDefaultAcceptedMimeTypes = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
];

const kDefaultMaxSizeMB = 10;

// Pre-built `SupportedFileFormats` for the default image MIME list.
// FileUploader formats `Object.keys(formats)` as the human-readable
// part of validation errors, so the key "image" controls the error
// copy ("Only IMAGE files are supported.").
const kDefaultSupportedFormats: SupportedFileFormats = {
  image: kDefaultAcceptedMimeTypes,
};

// ─── Types ──────────────────────────────────────────────────────────────────

interface PendingCropFile {
  file: File;
  objectUrl: string;
  naturalAspectRatio?: number;
}

export interface ImageUploaderChildProps {
  /** Opens the native file picker. */
  onClick: () => void;
  /** True while the cropper modal is open. */
  isCropping: boolean;
}

export interface ImageUploaderProps {
  /**
   * Crop configuration. Omit to bypass cropping — the picked File is
   * handed straight to `onFile`. GIFs always bypass cropping to
   * preserve animation, even when `crop` is set.
   */
  crop?: CropConfig;
  /**
   * Accepted MIME types. Defaults to the standard image set
   * (JPG / PNG / WebP / GIF). Pass a narrower list to restrict.
   */
  acceptedMimeTypes?: string[];
  /** Max file size in MB. Default 10. */
  maxSizeMB?: number;
  /**
   * Called with the FINAL `File` — cropped output when a crop config
   * was supplied, original file otherwise. ImageUploader doesn't
   * upload — the caller decides what to do with the bytes (push to
   * media-gallery via `useMediaUpload`, save locally, attach to a
   * form, etc).
   */
  onFile: (file: File) => void;
  /** Validation / crop error surfaces here as a ready-to-display string. */
  onError?: (message: string) => void;
  /** Render prop — receives the trigger and the isCropping flag. */
  children: (props: ImageUploaderChildProps) => ReactElement;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Headless single-image picker with optional cropping. Mirrors
 * PopshopRN's `flows/bio/components/ImageUploader.tsx` but stripped
 * down to just picking + cropping — uploading is intentionally NOT
 * baked in. The caller receives the final `File` via `onFile` and
 * decides how to persist it (typically through `useMediaUpload`).
 *
 * Internally:
 *   - {@link FileUploader} owns the file input + MIME / size
 *     validation. Auto-upload is disabled (`uploadFileOnChange: false`)
 *     so the picked File flows back through `onChange`.
 *   - {@link ImageCropperDialog} owns the crop UX when `crop` is set.
 *
 * The crop / dismiss orchestration that lived in the legacy
 * `ImagePickerProvider` (libs/ui/src/molecules) was moved here.
 * That module name was misleading (no React context involved) — this
 * component replaces it for image use cases; other consumers of
 * `ImagePickerProvider` will migrate later.
 */
export function ImageUploader({
  crop,
  acceptedMimeTypes,
  maxSizeMB = kDefaultMaxSizeMB,
  onFile,
  onError,
  children,
}: ImageUploaderProps): ReactElement {
  const [pendingCrop, setPendingCrop] = useState<PendingCropFile | null>(null);

  // Resolve the FileUploader `supportedFormats` config from the
  // caller's `acceptedMimeTypes` (defaults to the standard image set).
  const supportedFormats: SupportedFileFormats =
    acceptedMimeTypes != null
      ? { image: acceptedMimeTypes }
      : kDefaultSupportedFormats;

  // ── File picked ───────────────────────────────────────────────────────

  const handlePicked = useCallback(
    async (info: FileInfo | undefined) => {
      if (info == null || info.source !== 'local' || info.localFile == null) {
        return;
      }
      const file = await toPreviewableFile(info.localFile);

      // No crop config — or GIF (cropping flattens animation) — fall
      // through to the consumer with the original file.
      if (crop == null || file.type === 'image/gif') {
        onFile(file);
        return;
      }

      // Open the cropper. Probe the natural aspect ratio first so the
      // cropper can render a checkbox to "use original aspect ratio"
      // when `crop.allowPreserveImageAspectRatio` is set. The probe
      // is best-effort; if it fails the cropper falls back to a
      // default container size.
      const objectUrl = URL.createObjectURL(file);
      let naturalAspectRatio: number | undefined;
      try {
        const image = await loadImageElement(objectUrl);
        naturalAspectRatio = image.width / image.height;
      } catch {
        // ignore — naturalAspectRatio stays undefined
      }

      setPendingCrop({ file, objectUrl, naturalAspectRatio });
    },
    [crop, onFile],
  );

  // ── Crop completed (matches ImageCropperDialog's legacy callback) ─────

  const handleCropped = useCallback(
    (error: Error | undefined, croppedObjectUrl: string | undefined) => {
      if (pendingCrop == null) return;

      const original = pendingCrop.file;
      URL.revokeObjectURL(pendingCrop.objectUrl);
      setPendingCrop(null);

      // Crop failed or was canceled with no result — fall back to
      // the original file so the consumer at least gets something.
      if (error != null || croppedObjectUrl == null) {
        onFile(original);
        return;
      }

      // Convert the cropped object URL back into a File. Naming the
      // file after the original preserves the user-facing filename
      // (consumers often render it). Type is forced to `image/jpeg`
      // because the cropper's canvas encodes as JPEG.
      fetch(croppedObjectUrl)
        .then(response => response.blob())
        .then(blob => {
          URL.revokeObjectURL(croppedObjectUrl);
          const croppedFile = new File([blob], original.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          onFile(croppedFile);
        })
        .catch(() => {
          // Conversion is best-effort; fall back to original on failure.
          onFile(original);
        });
    },
    [onFile, pendingCrop],
  );

  // ── Crop dismissed ────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    if (pendingCrop) URL.revokeObjectURL(pendingCrop.objectUrl);
    setPendingCrop(null);
  }, [pendingCrop]);

  // ── FileUploader error → string for the consumer ──────────────────────

  const handleFileUploaderError = useCallback(
    (uploaderError: unknown) => {
      if (onError == null) return;
      onError(
        uploaderError instanceof Error
          ? uploaderError.message
          : 'Failed to process image.',
      );
    },
    [onError],
  );

  return (
    <>
      <FileUploader
        supportedFormats={supportedFormats}
        maxImageSizeInMB={maxSizeMB}
        uploadFileOnChange={false}
        onChange={handlePicked}
        onError={handleFileUploaderError}
      >
        {picker =>
          children({ onClick: picker.onClick, isCropping: pendingCrop != null })
        }
      </FileUploader>

      {/* The crop dialog only mounts when a `crop` config exists —
          without it the picked file flows straight through. */}
      {crop && (
        <ImageCropperDialog
          image={pendingCrop?.objectUrl}
          aspectRatio={crop.aspectRatio}
          naturalAspectRatio={pendingCrop?.naturalAspectRatio}
          allowPreserveImageAspectRatio={crop.allowPreserveImageAspectRatio}
          cropShape={crop.cropShape}
          onCropped={handleCropped}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const isHeicFile = (file: File): boolean =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  (!file.type && /\.(heic|heif)$/i.test(file.name));

// Chrome can't decode HEIC/HEIF into an <img> preview or a canvas crop
// source, so convert to JPEG up front — before the object URL feeding the
// natural-aspect-ratio probe and the cropper dialog is created.
async function toPreviewableFile(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;

  try {
    const jpegBlob = await heicToJpegBlob(file);
    return new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
      type: 'image/jpeg',
    });
  } catch (err) {
    console.warn('heicToJpegBlob failed', {
      name: file.name,
      type: file.type,
      err,
    });
    return file;
  }
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}
